import React, {useEffect, useRef, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    LinearProgress,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    TextField,
    Typography,
} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import DescriptionIcon from "@material-ui/icons/Description";
import HistoryIcon from "@material-ui/icons/History";
import TuneIcon from "@material-ui/icons/Tune";
import PersonIcon from "@material-ui/icons/Person";
import PublishIcon from "@material-ui/icons/Publish";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {useTranslation} from "react-i18next";
import Flex from "components/grid/Flex";
import * as colors from "components/colors/Colors";
import {copyToClipboard, uuid} from "utils/Utils";
import Sidebar from "../../components/sidebar/Sidebar";
import {Navigate} from "react-router";
import {LANDING} from "routes/routes";
import {
    useDownloadAnonymizedClinicalReportMutation,
    useGetClinicalReportAnonymizationGlobalSettingQuery,
    useGetClinicalReportAnonymizationJobMutation,
    useGetClinicalReportSummaryAvailabilityQuery,
    useSaveClinicalReportCopyConfirmationMutation,
    useSubmitClinicalReportAnonymizationMutation,
    useSummarizeClinicalReportsMutation,
} from "./DoctorsLetterApi";
import ClinicalReportCopyConfirmationDialog from "./ClinicalReportCopyConfirmationDialog";
import {useGetAiTokenUsageQuery} from "./AiTokenUsageApi";
import ClinicalReportSummaryResult from "./ClinicalReportSummaryResult";
import ClinicalReportFileList, {
    ANONYMIZATION_DONE,
    ANONYMIZATION_PENDING,
    ANONYMIZATION_UNPROCESSABLE,
} from "./ClinicalReportFileList";
import ClinicalReportDropZone from "./ClinicalReportDropZone";
import {asArray, asText} from "./ClinicalReportSummaryParts";
import {isClinicalReportAnonymizationEnabled} from "./ClinicalReportAnonymizationPreference";

const PATIENT_SEXES = ["MALE", "FEMALE"];
const SUMMARY_MODES = ["COMPACT", "DETAILED"];
const RECOMMENDED_SUMMARY_MODE = "COMPACT";
const COMPARISON_OPTIONS = ["C1", "C2", "C3", "C4"];
const MAXIMUM_PATIENT_AGE_DIGITS = 3;
const NO_FINDINGS_STATUS = 422;
const ANONYMIZATION_POLLING_INTERVAL_MILLISECONDS = 5000;
const ANONYMIZATION_MAXIMUM_DURATION_MILLISECONDS = 10 * 60 * 1000;
const ANONYMIZED_FILE_TYPE = "application/pdf";
const COPY_NOTICE_DURATION_MILLISECONDS = 2500;

const useStyles = makeStyles((theme) => ({
    root: {
        marginLeft: 60,
        height: "100vh",
        flexWrap: "nowrap",
        background: colors.clinicalPageBackground,
    },
    inputColumn: {
        flex: "0 0 400px",
        flexDirection: "column",
        gap: theme.spacing(1.25),
        padding: theme.spacing(2),
        overflowY: "auto",
        background: colors.clinicalSurface,
        borderRight: `1px solid ${colors.clinicalOutline}`,
    },
    resultColumn: {
        flex: "1 1 auto",
        minWidth: 0,
        flexDirection: "column",
        gap: theme.spacing(2),
        padding: theme.spacing(3),
        overflowY: "auto",
    },
    aiTokenUsageBar: {
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        background: colors.clinicalSurfaceContainer,
        borderRadius: 10,
    },
    aiTokenUsageText: {
        fontSize: "0.8125rem",
        color: colors.clinicalMutedText,
    },
    aiTokenUsageCount: {
        fontWeight: 600,
        color: colors.clinicalPrimaryBlue,
    },
    newReportButton: {
        alignSelf: "stretch",
        textTransform: "none",
        fontWeight: 600,
        borderColor: colors.clinicalPrimaryBlue,
        color: colors.clinicalPrimaryBlue,
    },
    patientRow: {
        display: "grid",
        gridTemplateColumns: "90px 1fr",
        gap: theme.spacing(1),
        alignItems: "start",
    },
    card: {
        flexDirection: "column",
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
        background: colors.htmlWhite,
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
    },
    cardHead: {
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
    cardTitle: {
        alignItems: "center",
        gap: theme.spacing(1),
        fontWeight: 600,
        fontSize: "1rem",
    },
    cardTitleIcon: {
        color: colors.clinicalPrimaryBlue,
    },
    cardDescription: {
        fontSize: "0.8rem",
        color: colors.clinicalMutedText,
    },
    countChip: {
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: "0.75rem",
        fontFamily: "monospace",
        color: colors.clinicalPrimaryBlue,
        background: colors.clinicalSurfaceContainer,
        whiteSpace: "nowrap",
    },
    deltaChip: {
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.75rem",
        fontWeight: 600,
        color: colors.clinicalDeltaGreenText,
        background: colors.clinicalDeltaGreenBackground,
        whiteSpace: "nowrap",
    },
    fileListLabel: {
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: colors.clinicalMutedText,
    },
    optionBox: {
        margin: 0,
        padding: theme.spacing(1, 1.5),
        borderRadius: 8,
        background: colors.clinicalSurfaceContainer,
        alignItems: "flex-start",
    },
    optionTitle: {
        fontWeight: 600,
        fontSize: "0.875rem",
    },
    optionDescription: {
        fontSize: "0.8rem",
        color: colors.clinicalMutedText,
    },
    recommendedChip: {
        marginLeft: theme.spacing(1),
        padding: "1px 6px",
        borderRadius: 4,
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: colors.htmlWhite,
        background: colors.clinicalPrimaryBlue,
    },
    summaryModeGroup: {
        gap: theme.spacing(1),
    },
    synthesizeButton: {
        padding: theme.spacing(1.5),
        borderRadius: 10,
        textTransform: "none",
        fontSize: "1rem",
        fontWeight: 600,
        background: colors.clinicalPrimaryBlue,
        color: colors.htmlWhite,
        "&:hover": {background: colors.clinicalPrimaryBlueHover},
    },
    progressBox: {
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    emptyResult: {
        margin: "auto",
        maxWidth: 420,
        textAlign: "center",
        color: colors.clinicalMutedText,
    },
}));

const newClinicalReportFile = (droppedFile, anonymizationState) => ({
    id: uuid(),
    fileName: droppedFile.name,
    file: droppedFile,
    anonymizationState,
});

const waitMilliseconds = (waitDurationMilliseconds) => new Promise((resolve) => setTimeout(resolve, waitDurationMilliseconds));

const ClinicalReportSummarizer = () => {
    const classes = useStyles();
    const {t: translate, i18n} = useTranslation();

    const [patientAge, setPatientAge] = useState("");
    const [patientSex, setPatientSex] = useState("");
    const [currentReportFiles, setCurrentReportFiles] = useState([]);
    const [comparisonReportFiles, setComparisonReportFiles] = useState([]);
    const [summaryMode, setSummaryMode] = useState(RECOMMENDED_SUMMARY_MODE);
    const [clinicalReportSummary, setClinicalReportSummary] = useState(null);
    const [editedLetterTexts, setEditedLetterTexts] = useState({});
    const [summaryErrorKey, setSummaryErrorKey] = useState(null);
    const [copyNoticeKey, setCopyNoticeKey] = useState(null);
    const [isSourceDocumentsConfirmed, setIsSourceDocumentsConfirmed] = useState(false);
    const [summarizedFileNames, setSummarizedFileNames] = useState([]);
    const [isCopyConfirmed, setIsCopyConfirmed] = useState(false);
    const [pendingCopyLetterText, setPendingCopyLetterText] = useState(null);

    const anonymizingClinicalReportFileIdsRef = useRef(new Set());

    const [summarizeClinicalReports, {isLoading: summarizing}] = useSummarizeClinicalReportsMutation();
    const [submitClinicalReportAnonymization] = useSubmitClinicalReportAnonymizationMutation();
    const [getClinicalReportAnonymizationJob] = useGetClinicalReportAnonymizationJobMutation();
    const [downloadAnonymizedClinicalReport] = useDownloadAnonymizedClinicalReportMutation();
    const [saveClinicalReportCopyConfirmation] = useSaveClinicalReportCopyConfirmationMutation();
    const {data: isGlobalAnonymizationEnabled} = useGetClinicalReportAnonymizationGlobalSettingQuery();
    const {data: aiTokenUsage} = useGetAiTokenUsageQuery();
    const {data: isClinicalReportSummaryAvailable} = useGetClinicalReportSummaryAvailabilityQuery();

    useEffect(() => {
        const anonymizingClinicalReportFileIds = anonymizingClinicalReportFileIdsRef.current;
        return () => anonymizingClinicalReportFileIds.clear();
    }, []);

    const anonymizedCurrentReportFiles = currentReportFiles.filter((currentReportFile) => currentReportFile.anonymizationState === ANONYMIZATION_DONE);
    const anonymizedComparisonReportFiles = comparisonReportFiles.filter((comparisonReportFile) => comparisonReportFile.anonymizationState === ANONYMIZATION_DONE);
    const isAnonymizationPending = [...currentReportFiles, ...comparisonReportFiles]
        .some((clinicalReportFile) => clinicalReportFile.anonymizationState === ANONYMIZATION_PENDING);
    const hasComparisonReportFiles = comparisonReportFiles.length > 0;
    const isPatientSexMissing = !patientSex;
    const canSummarize = anonymizedCurrentReportFiles.length > 0 && !isAnonymizationPending && !isPatientSexMissing && !summarizing
        && isSourceDocumentsConfirmed;
    const unreadableFileNames = asArray(clinicalReportSummary?.quality?.unreadableFileNames).map(asText).filter(Boolean);
    const currentMonthAiTokens = Number(aiTokenUsage?.currentMonthTokens);
    const hasCurrentMonthAiTokens = aiTokenUsage != null && Number.isFinite(currentMonthAiTokens);

    const anonymizeClinicalReportFile = async (clinicalReportFile, setClinicalReportFiles) => {
        const isStillAnonymizing = () => anonymizingClinicalReportFileIdsRef.current.has(clinicalReportFile.id);
        const finishAnonymization = (anonymizedFields) => {
            if (!isStillAnonymizing()) {
                return;
            }
            anonymizingClinicalReportFileIdsRef.current.delete(clinicalReportFile.id);
            setClinicalReportFiles((previousClinicalReportFiles) => previousClinicalReportFiles.map((previousClinicalReportFile) =>
                previousClinicalReportFile.id === clinicalReportFile.id ? {...previousClinicalReportFile, ...anonymizedFields} : previousClinicalReportFile));
        };

        try {
            const anonymizationJob = await submitClinicalReportAnonymization(clinicalReportFile.file).unwrap();
            if (!anonymizationJob?.jobId) {
                finishAnonymization({anonymizationState: ANONYMIZATION_UNPROCESSABLE});
                return;
            }
            const anonymizationDeadline = Date.now() + ANONYMIZATION_MAXIMUM_DURATION_MILLISECONDS;
            while (isStillAnonymizing() && Date.now() < anonymizationDeadline) {
                await waitMilliseconds(ANONYMIZATION_POLLING_INTERVAL_MILLISECONDS);
                if (!isStillAnonymizing()) {
                    return;
                }
                const polledAnonymizationJob = await getClinicalReportAnonymizationJob(anonymizationJob.jobId).unwrap().catch(() => null);
                if (polledAnonymizationJob?.state === ANONYMIZATION_DONE) {
                    const anonymizedClinicalReportBlob = await downloadAnonymizedClinicalReport(anonymizationJob.jobId).unwrap();
                    finishAnonymization({
                        file: new File([anonymizedClinicalReportBlob], clinicalReportFile.fileName, {type: ANONYMIZED_FILE_TYPE}),
                        anonymizationState: ANONYMIZATION_DONE,
                    });
                    return;
                }
                if (polledAnonymizationJob?.state === ANONYMIZATION_UNPROCESSABLE) {
                    finishAnonymization({anonymizationState: ANONYMIZATION_UNPROCESSABLE});
                    return;
                }
            }
            finishAnonymization({anonymizationState: ANONYMIZATION_UNPROCESSABLE});
        } catch (anonymizationError) {
            finishAnonymization({anonymizationState: ANONYMIZATION_UNPROCESSABLE});
        }
    };

    const addClinicalReportFiles = (droppedFiles, setClinicalReportFiles) => {
        if (!isClinicalReportAnonymizationEnabled(isGlobalAnonymizationEnabled)) {
            const unanonymizedClinicalReportFiles = droppedFiles.map((droppedFile) => newClinicalReportFile(droppedFile, ANONYMIZATION_DONE));
            setClinicalReportFiles((previousClinicalReportFiles) => [...previousClinicalReportFiles, ...unanonymizedClinicalReportFiles]);
            return;
        }
        const droppedClinicalReportFiles = droppedFiles.map((droppedFile) => newClinicalReportFile(droppedFile, ANONYMIZATION_PENDING));
        droppedClinicalReportFiles.forEach((droppedClinicalReportFile) =>
            anonymizingClinicalReportFileIdsRef.current.add(droppedClinicalReportFile.id));
        setClinicalReportFiles((previousClinicalReportFiles) => [...previousClinicalReportFiles, ...droppedClinicalReportFiles]);
        droppedClinicalReportFiles.forEach((droppedClinicalReportFile) =>
            anonymizeClinicalReportFile(droppedClinicalReportFile, setClinicalReportFiles));
    };

    const startNewReport = () => {
        anonymizingClinicalReportFileIdsRef.current.clear();
        setPatientAge("");
        setPatientSex("");
        setCurrentReportFiles([]);
        setComparisonReportFiles([]);
        setSummaryMode(RECOMMENDED_SUMMARY_MODE);
        setClinicalReportSummary(null);
        setEditedLetterTexts({});
        setSummaryErrorKey(null);
        setCopyNoticeKey(null);
        setIsSourceDocumentsConfirmed(false);
        setSummarizedFileNames([]);
        setIsCopyConfirmed(false);
        setPendingCopyLetterText(null);
    };

    const onPatientAgeChange = (patientAgeChangeEvent) => {
        setPatientAge(patientAgeChangeEvent.target.value.replace(/\D/g, "").slice(0, MAXIMUM_PATIENT_AGE_DIGITS));
    };

    const onCurrentReportFilesDrop = (droppedFiles) => {
        if (!droppedFiles?.length) {
            return;
        }
        addClinicalReportFiles(droppedFiles, setCurrentReportFiles);
    };

    const onComparisonReportFilesDrop = (droppedFiles) => {
        if (!droppedFiles?.length) {
            return;
        }
        addClinicalReportFiles(droppedFiles, setComparisonReportFiles);
    };

    const removeCurrentReportFile = (clinicalReportFileId) => {
        anonymizingClinicalReportFileIdsRef.current.delete(clinicalReportFileId);
        setCurrentReportFiles((previousCurrentReportFiles) =>
            previousCurrentReportFiles.filter((currentReportFile) => currentReportFile.id !== clinicalReportFileId));
    };

    const removeComparisonReportFile = (clinicalReportFileId) => {
        anonymizingClinicalReportFileIdsRef.current.delete(clinicalReportFileId);
        setComparisonReportFiles((previousComparisonReportFiles) =>
            previousComparisonReportFiles.filter((comparisonReportFile) => comparisonReportFile.id !== clinicalReportFileId));
    };

    const onSynthesize = () => {
        if (!canSummarize) {
            return;
        }
        setSummaryErrorKey(null);
        const synthesizedFileNames = [...anonymizedCurrentReportFiles, ...anonymizedComparisonReportFiles]
            .map((anonymizedClinicalReportFile) => anonymizedClinicalReportFile.fileName);
        summarizeClinicalReports({
            currentReportFiles: anonymizedCurrentReportFiles.map((anonymizedCurrentReportFile) => anonymizedCurrentReportFile.file),
            comparisonReportFiles: anonymizedComparisonReportFiles.map((anonymizedComparisonReportFile) => anonymizedComparisonReportFile.file),
            patientAge,
            patientSex,
            summaryMode,
            comparisonOptions: anonymizedComparisonReportFiles.length > 0 ? COMPARISON_OPTIONS : [],
            sourceDocumentsConfirmationText: translate("clinicalReportSummarizer.confirmation.sourceDocumentsRead"),
        })
            .unwrap()
            .then((summarizedClinicalReport) => {
                setClinicalReportSummary(summarizedClinicalReport && typeof summarizedClinicalReport === "object" ? summarizedClinicalReport : null);
                setEditedLetterTexts({});
                setSummarizedFileNames(synthesizedFileNames);
                setIsCopyConfirmed(false);
            })
            .catch((summaryError) => {
                setSummaryErrorKey(summaryError?.status === NO_FINDINGS_STATUS
                    ? "clinicalReportSummarizer.error.noFindings"
                    : "clinicalReportSummarizer.error.failed");
            });
    };

    const changeLetterText = (letterTextKey, letterText) => {
        setEditedLetterTexts((previousEditedLetterTexts) => ({...previousEditedLetterTexts, [letterTextKey]: letterText}));
    };

    const resetLetterText = (letterTextKey) => {
        setEditedLetterTexts((previousEditedLetterTexts) => Object.fromEntries(
            Object.entries(previousEditedLetterTexts)
                .filter(([editedLetterTextKey]) => editedLetterTextKey !== letterTextKey)));
    };

    const copyLetterTextToClipboard = (letterText) => {
        const isLetterTextCopied = copyToClipboard(letterText);
        setCopyNoticeKey(isLetterTextCopied
            ? "clinicalReportSummarizer.copy.success"
            : "clinicalReportSummarizer.copy.failure");
        return isLetterTextCopied;
    };

    const copyLetterText = (letterText) => {
        if (!isCopyConfirmed) {
            setPendingCopyLetterText(letterText);
            return;
        }
        copyLetterTextToClipboard(letterText);
    };

    const confirmCopy = (confirmationTexts) => {
        const isLetterTextCopied = copyLetterTextToClipboard(pendingCopyLetterText);
        setPendingCopyLetterText(null);
        saveClinicalReportCopyConfirmation({
            confirmationTexts,
            fileNames: summarizedFileNames,
            summaryHash: clinicalReportSummary?.summaryHash,
        })
            .unwrap()
            .then(() => setIsCopyConfirmed(true))
            .catch(() => setCopyNoticeKey(isLetterTextCopied
                ? "clinicalReportSummarizer.copy.confirmationFailure"
                : "clinicalReportSummarizer.copy.failure"));
    };

    if (isClinicalReportSummaryAvailable === false) {
        return <Navigate to={LANDING.path}/>;
    }

    return (
        <>
            <Sidebar />
            <Flex container className={classes.root}>
                <Flex container className={classes.inputColumn}>
                    {hasCurrentMonthAiTokens && (
                        <Flex container className={classes.aiTokenUsageBar}>
                            <Typography className={classes.aiTokenUsageText}>
                                {translate("clinicalReportSummarizer.aiTokenUsage.currentMonth")}
                            </Typography>
                            <Typography className={`${classes.aiTokenUsageText} ${classes.aiTokenUsageCount}`}>
                                {translate("clinicalReportSummarizer.aiTokenUsage.tokenCount", {
                                    tokenCount: new Intl.NumberFormat(i18n.language).format(currentMonthAiTokens),
                                })}
                            </Typography>
                        </Flex>
                    )}
                    <Button
                        variant={"outlined"}
                        className={classes.newReportButton}
                        startIcon={<NoteAddIcon />}
                        onClick={startNewReport}
                        disabled={summarizing}
                    >
                        {translate("clinicalReportSummarizer.newReport")}
                    </Button>

                    <Flex container className={classes.card}>
                        <Flex container className={classes.cardHead}>
                            <Flex container className={classes.cardTitle}>
                                <DescriptionIcon className={classes.cardTitleIcon} />
                                {translate("clinicalReportSummarizer.currentReports.title")}
                            </Flex>
                            <span className={classes.countChip}>
                                {translate("clinicalReportSummarizer.currentReports.documentCount", {count: currentReportFiles.length})}
                            </span>
                        </Flex>
                        <ClinicalReportDropZone
                            onDrop={onCurrentReportFilesDrop}
                            DropZoneIcon={PublishIcon}
                            dropZoneAction={translate("clinicalReportSummarizer.currentReports.dropZoneAction")}
                            dropZoneText={translate("clinicalReportSummarizer.currentReports.dropZoneText")}
                            dropZoneHelp={translate("clinicalReportSummarizer.currentReports.dropZoneHelp")}
                        />
                        {currentReportFiles.length > 0 && (
                            <Typography className={classes.fileListLabel}>{translate("clinicalReportSummarizer.currentReports.listTitle")}</Typography>
                        )}
                        <ClinicalReportFileList
                            clinicalReportFiles={currentReportFiles}
                            unreadableFileNames={unreadableFileNames}
                            onRemove={removeCurrentReportFile}
                        />
                    </Flex>

                    <Flex container className={classes.card}>
                        <Flex container className={classes.cardHead}>
                            <Flex container className={classes.cardTitle}>
                                <HistoryIcon className={classes.cardTitleIcon} />
                                {translate("clinicalReportSummarizer.comparisonReports.title")}
                            </Flex>
                            {hasComparisonReportFiles && (
                                <span className={classes.deltaChip}>{translate("clinicalReportSummarizer.comparisonReports.deltaActive")}</span>
                            )}
                        </Flex>
                        <ClinicalReportDropZone
                            onDrop={onComparisonReportFilesDrop}
                            DropZoneIcon={TrendingUpIcon}
                            dropZoneAction={translate("clinicalReportSummarizer.comparisonReports.dropZoneAction")}
                            dropZoneText={translate("clinicalReportSummarizer.comparisonReports.dropZoneText")}
                            dropZoneHelp={translate("clinicalReportSummarizer.comparisonReports.dropZoneHelp")}
                        />
                        <ClinicalReportFileList
                            clinicalReportFiles={comparisonReportFiles}
                            unreadableFileNames={unreadableFileNames}
                            onRemove={removeComparisonReportFile}
                        />
                    </Flex>

                    <Flex container className={classes.card}>
                        <Flex container className={classes.cardTitle}>
                            <PersonIcon className={classes.cardTitleIcon} />
                            {translate("clinicalReportSummarizer.patient.title")}
                        </Flex>
                        <div className={classes.patientRow}>
                            <TextField
                                variant={"outlined"}
                                size={"small"}
                                placeholder={translate("clinicalReportSummarizer.patient.age")}
                                value={patientAge}
                                onChange={onPatientAgeChange}
                                inputProps={{inputMode: "numeric"}}
                            />
                            <FormControl variant={"outlined"} size={"small"} error={isPatientSexMissing}>
                                <Select
                                    value={patientSex}
                                    onChange={(patientSexChangeEvent) => setPatientSex(patientSexChangeEvent.target.value)}
                                    displayEmpty
                                >
                                    <MenuItem value={""} disabled>{translate("clinicalReportSummarizer.patient.sexPlaceholder")}</MenuItem>
                                    {PATIENT_SEXES.map((selectablePatientSex) => (
                                        <MenuItem key={selectablePatientSex} value={selectablePatientSex}>
                                            {translate(`clinicalReportSummarizer.patientSex.${selectablePatientSex}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {isPatientSexMissing && (
                                    <FormHelperText>{translate("clinicalReportSummarizer.patient.sexRequired")}</FormHelperText>
                                )}
                            </FormControl>
                        </div>
                    </Flex>

                    <Flex container className={classes.card}>
                        <Flex container className={classes.cardTitle}>
                            <TuneIcon className={classes.cardTitleIcon} />
                            {translate("clinicalReportSummarizer.summaryMode.title")}
                        </Flex>
                        <RadioGroup
                            className={classes.summaryModeGroup}
                            value={summaryMode}
                            onChange={(summaryModeChangeEvent) => setSummaryMode(summaryModeChangeEvent.target.value)}
                        >
                            {SUMMARY_MODES.map((selectableSummaryMode) => (
                                <FormControlLabel
                                    key={selectableSummaryMode}
                                    value={selectableSummaryMode}
                                    className={classes.optionBox}
                                    control={<Radio color={"primary"} size={"small"} />}
                                    label={
                                        <Flex container direction={"column"}>
                                            <Typography className={classes.optionTitle}>
                                                {translate(`clinicalReportSummarizer.summaryMode.${selectableSummaryMode}.title`)}
                                                {selectableSummaryMode === RECOMMENDED_SUMMARY_MODE && (
                                                    <span className={classes.recommendedChip}>{translate("clinicalReportSummarizer.summaryMode.recommended")}</span>
                                                )}
                                            </Typography>
                                            <Typography className={classes.optionDescription}>
                                                {translate(`clinicalReportSummarizer.summaryMode.${selectableSummaryMode}.description`)}
                                            </Typography>
                                        </Flex>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </Flex>

                    <FormControlLabel
                        control={
                            <Checkbox
                                color={"primary"}
                                checked={isSourceDocumentsConfirmed}
                                onChange={(sourceDocumentsCheckboxChangeEvent) => setIsSourceDocumentsConfirmed(sourceDocumentsCheckboxChangeEvent.target.checked)}
                            />
                        }
                        label={translate("clinicalReportSummarizer.confirmation.sourceDocumentsRead")}
                    />
                    <Button
                        variant={"contained"}
                        fullWidth
                        className={classes.synthesizeButton}
                        startIcon={<AutoAwesomeIcon />}
                        disabled={!canSummarize}
                        onClick={onSynthesize}
                    >
                        {translate("clinicalReportSummarizer.synthesize")}
                    </Button>
                    {summarizing && (
                        <Flex container className={classes.progressBox}>
                            <Typography className={classes.cardDescription}>{translate("clinicalReportSummarizer.synthesizing")}</Typography>
                            <LinearProgress />
                        </Flex>
                    )}
                    {summaryErrorKey && <Alert severity={"error"}>{translate(summaryErrorKey)}</Alert>}
                </Flex>

                <Flex container className={classes.resultColumn}>
                    {summarizing && <LinearProgress />}
                    {clinicalReportSummary ? (
                        <ClinicalReportSummaryResult
                            clinicalReportSummary={clinicalReportSummary}
                            editedLetterTexts={editedLetterTexts}
                            onLetterTextChange={changeLetterText}
                            onLetterTextReset={resetLetterText}
                            onLetterTextCopy={copyLetterText}
                            isLetterTextCopyConfirmed={isCopyConfirmed}
                        />
                    ) : (
                        <Typography className={classes.emptyResult}>
                            {translate(summarizing ? "clinicalReportSummarizer.synthesizing" : "clinicalReportSummarizer.result.empty")}
                        </Typography>
                    )}
                </Flex>
            </Flex>
            <Snackbar
                open={!!copyNoticeKey}
                autoHideDuration={COPY_NOTICE_DURATION_MILLISECONDS}
                onClose={() => setCopyNoticeKey(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity={copyNoticeKey === "clinicalReportSummarizer.copy.success" ? "success" : "warning"} onClose={() => setCopyNoticeKey(null)}>
                    {copyNoticeKey && translate(copyNoticeKey)}
                </Alert>
            </Snackbar>
            <ClinicalReportCopyConfirmationDialog
                open={pendingCopyLetterText !== null}
                findings={clinicalReportSummary?.findings}
                onConfirm={confirmCopy}
                onCancel={() => setPendingCopyLetterText(null)}
            />
        </>
    );
};

export default ClinicalReportSummarizer;
